// Maps LaTeX commands the medical assistant tends to emit to their Unicode form.
const LATEX_TO_UNICODE: Record<string, string> = {
  alpha: 'α', beta: 'β', gamma: 'γ', delta: 'δ', epsilon: 'ε', zeta: 'ζ',
  eta: 'η', theta: 'θ', iota: 'ι', kappa: 'κ', lambda: 'λ', mu: 'µ', nu: 'ν',
  xi: 'ξ', pi: 'π', rho: 'ρ', sigma: 'σ', tau: 'τ', phi: 'φ', chi: 'χ',
  psi: 'ψ', omega: 'ω',
  Gamma: 'Γ', Delta: 'Δ', Theta: 'Θ', Lambda: 'Λ', Xi: 'Ξ', Pi: 'Π',
  Sigma: 'Σ', Phi: 'Φ', Psi: 'Ψ', Omega: 'Ω',
  times: '×', pm: '±', le: '≤', leq: '≤', ge: '≥', geq: '≥', approx: '≈',
  cdot: '·', circ: '°', deg: '°', degree: '°',
};

// Ion charges (Na^+ → Na⁺) and powers (10^9 → 10⁹).
const SUPERSCRIPTS: Record<string, string> = {
  '+': '⁺', '-': '⁻', '−': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³',
  '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', n: 'ⁿ',
};

const SUBSCRIPTS: Record<string, string> = {
  '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
  '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
};

// Replace LaTeX commands (\gamma, \times, …) with Unicode; leave unknown commands as-is.
function convertCommands(s: string): string {
  return s.replace(/\\([A-Za-z]+)/g, (match, cmd) => LATEX_TO_UNICODE[cmd] ?? match);
}

/**
 * `Na^+` → `Na⁺`, `10^9` → `10⁹`, `H_2O` → `H₂O`, including the braced form
 * LaTeX uses for multi-character scripts (`10^{-9}`). A script that isn't fully
 * mappable is left alone rather than half-converted.
 */
function convertScripts(s: string): string {
  const map = (chars: string, table: Record<string, string>): string | null => {
    let out = '';
    for (const ch of chars) {
      const mapped = table[ch];
      if (!mapped) return null;
      out += mapped;
    }
    return out;
  };

  return s
    .replace(
      /\^\{([^}]+)\}|\^(\S)/g,
      (match, braced, single) => map(braced ?? single, SUPERSCRIPTS) ?? match,
    )
    .replace(
      /_\{([^}]+)\}|_(\S)/g,
      (match, braced, single) => map(braced ?? single, SUBSCRIPTS) ?? match,
    );
}

// What makes a `$...$` span math rather than money: a LaTeX command, or a
// super/subscript (`$Na^+$`, `$H_2O$`). Prices — `$5`, `$10.99` — have neither,
// which is what keeps them out of the unwrapping below.
const LOOKS_LIKE_MATH = /[\\^_]/;

/**
 * Strip LaTeX math that LLMs leak into chat markdown (`$\gamma$-GT`, `$Na^+$`)
 * and convert it to Unicode. Price-safe: a `$...$` span is only unwrapped when
 * its CONTENTS look like math.
 */
export function delatexify(text: string): string {
  if (typeof text !== 'string' || !LOOKS_LIKE_MATH.test(text)) return text;
  let out = text
    // The math test is on the span's contents, so `cena to $5 do $10` keeps its
    // dollars while `$Na^+$` and `$5\times10^9$/l` are unwrapped.
    .replace(/\$([^$\n]*)\$/g, (match, inner) =>
      LOOKS_LIKE_MATH.test(inner) ? convertCommands(inner) : match,
    )
    // \( ... \) inline delimiters → convert inner, drop delimiters.
    .replace(/\\\(([\s\S]*?)\\\)/g, (_m, inner) => convertCommands(inner))
    // \[ ... \] display delimiters → convert inner, drop delimiters.
    .replace(/\\\[([\s\S]*?)\\\]/g, (_m, inner) => convertCommands(inner));
  // Any remaining bare LaTeX commands (known map only; unknowns untouched).
  out = convertCommands(out);
  // Scripts last, so ones freed from a `$...$` span are caught too.
  return convertScripts(out);
}
